import * as React from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSWRConfig } from 'swr'
import { Button } from '@/components/ui/button'
import { Notice } from '@/components/ui/notice'
import { EntryScreen, EntryTitle, FullPageStatus } from '@/components/visitone/entry-screen'
import { OperationFeedback } from '@/components/visitone/operation-feedback'
import { ProductMark } from '@/components/visitone/product-mark'
import { RolePills } from '@/components/visitone/surfaces/people/people-shared'
import { useT } from '@/components/visitone/visitone-provider'
import { hasHandOver, recordHandOver, takeHandOver, type HandOverMode } from '@/lib/auth/hand-over'
import { leaveAppFor } from '@/lib/auth/leave-app'
import {
  acceptInvitation,
  createInvitationAccount,
  invitationDetails,
  peekInvitation,
  type InvitationDetails,
  type InvitationPeek,
} from '@/lib/bff/invitations'
import { PeopleRequestError } from '@/lib/bff/people'
import { formatTenantDateTime } from '@/lib/format/tenant-date'
import { endSession, openTenant } from '@/lib/tenant/tenant-session'

/** What Auth0 reported on the way back from its set-password page. */
export type PasswordResult = 'set' | 'notSet' | 'unknown'
/**
 * Router state on the way back from Auth0: which hand-over the person is
 * returning from, what the set-password page reported, and whether a sign-in
 * started right after the password was set.
 */
export type InvitationRouteState = {
  invitationReturn?: HandOverMode
  passwordResult?: PasswordResult
  accountReady?: true
}
/** Router state on arriving in the shell after accepting: the tenant just joined. */
export type JoinedState = { joinedTenant?: string }

type UsablePeek = Extract<InvitationPeek, { usable: true }>

type ChooseNotice = 'existing' | 'busy' | 'passwordSet' | 'passwordNotSet' | 'passwordUnknown'

type View =
  | { kind: 'loading' }
  | { kind: 'settingUp' }
  | { kind: 'choose'; peek: UsablePeek; notice?: ChooseNotice }
  | { kind: 'createError'; peek: UsablePeek }
  | { kind: 'confirm'; details: InvitationDetails }
  | { kind: 'joining'; details: InvitationDetails }
  | { kind: 'mismatch'; details: InvitationDetails }
  | { kind: 'verify'; details: InvitationDetails; still: boolean }
  | { kind: 'invalid' }
  | { kind: 'error'; retry: () => void }

const code = (error: unknown) => (error instanceof PeopleRequestError ? error.code : null)
const status = (error: unknown) => (error instanceof PeopleRequestError ? error.status : null)
const notUsable = (error: unknown) => code(error) === 'invitation_not_usable' || status(error) === 404

/*
 * Only Auth0's `success` flag is read from the return; its `message` is
 * English-only and is never shown.
 */
function passwordResultOf(search: string): PasswordResult {
  const success = new URLSearchParams(search).get('success')
  if (success === 'true') return 'set'
  if (success === 'false') return 'notSet'
  return 'unknown'
}

const RETURN_NOTICES: Record<PasswordResult, ChooseNotice> = {
  set: 'passwordSet',
  notSet: 'passwordNotSet',
  unknown: 'passwordUnknown',
}

const NOTICES: Record<ChooseNotice, { tone: 'info' | 'warning'; signInOnly: boolean }> = {
  existing: { tone: 'info', signInOnly: true },
  passwordSet: { tone: 'info', signInOnly: true },
  passwordNotSet: { tone: 'warning', signInOnly: false },
  passwordUnknown: { tone: 'info', signInOnly: false },
  busy: { tone: 'warning', signInOnly: false },
}

function required(accessToken: string | undefined): string {
  if (!accessToken) throw new PeopleRequestError(401, null, {}, 'No access token available')
  return accessToken
}

/*
 * /invitation/{token} (VIS-332, VIS-441). Outside AuthGate: before sign-in it
 * shows only what `peek` gives (the venue, a masked address, until when) and
 * never sends a bearer token. Create an account asks the BFF to set up the
 * account and then goes to Auth0's set-password page, which returns here.
 * Signed in, it shows the invitation and accepts it, or explains why it
 * can't. The token is only ever sent in a request body.
 */
export function InvitationRoute() {
  const t = useT()
  const { token = '' } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { mutate } = useSWRConfig()
  const { isLoading, isAuthenticated, user, loginWithRedirect, logout, getAccessTokenSilently, getIdTokenClaims } =
    useAuth0()
  const routeState = location.state as InvitationRouteState | null
  const path = `/invitation/${token}`
  const [view, setView] = React.useState<View>({ kind: 'loading' })
  const rootRef = React.useRef<HTMLDivElement>(null)
  const shownKind = React.useRef<View['kind']>('loading')
  const returnChecked = React.useRef(false)

  // Auth0's set-password page returns straight here, outside HandOverReturn:
  // use up its hand-over and drop the query, keeping only what it reported.
  React.useEffect(() => {
    if (returnChecked.current) return
    returnChecked.current = true
    const reported = new URLSearchParams(location.search).has('success')
    const waiting = hasHandOver() ? takeHandOver() : null
    const fromTicket = waiting?.mode === 'setPassword' && waiting.path === path
    if (waiting && !fromTicket) recordHandOver(waiting)
    if (!reported && !fromTicket) return
    navigate(path, {
      replace: true,
      state: { invitationReturn: 'setPassword', passwordResult: passwordResultOf(location.search) } satisfies InvitationRouteState,
    })
  }, [location.search, navigate, path])

  const loadPublic = React.useCallback(async () => {
    setView({ kind: 'loading' })
    try {
      const peek = await peekInvitation(token)
      setView(peek.usable ? { kind: 'choose', peek } : { kind: 'invalid' })
    } catch {
      setView({ kind: 'error', retry: () => void loadPublic() })
    }
  }, [token])

  const loadSignedIn = React.useCallback(async () => {
    setView({ kind: 'loading' })
    try {
      const details = await invitationDetails(required(await getAccessTokenSilently()), token)
      setView(details.emailMatches ? { kind: 'confirm', details } : { kind: 'mismatch', details })
    } catch (error) {
      if (notUsable(error)) setView({ kind: 'invalid' })
      else setView({ kind: 'error', retry: () => void loadSignedIn() })
    }
  }, [getAccessTokenSilently, token])

  React.useEffect(() => {
    if (isLoading) return
    if (isAuthenticated) void loadSignedIn()
    else void loadPublic()
  }, [isLoading, isAuthenticated, loadPublic, loadSignedIn])

  // Each new state moves focus to its heading, so the change is announced.
  React.useEffect(() => {
    if (view.kind === shownKind.current) return
    shownKind.current = view.kind
    const heading = rootRef.current?.querySelector<HTMLElement>('h1')
    if (!heading) return
    heading.setAttribute('tabindex', '-1')
    heading.focus()
  }, [view])

  const signIn = (accountReady = false) => {
    recordHandOver({ path, mode: 'signIn', ...(accountReady ? { accountReady: true } : {}) })
    void Promise.resolve(loginWithRedirect({ appState: { returnTo: path } })).catch(() =>
      setView({ kind: 'error', retry: () => void loadPublic() }),
    )
  }

  // The status screen replaces the buttons, so a second click can't send again.
  const createAccount = async (peek: UsablePeek) => {
    setView({ kind: 'settingUp' })
    try {
      const created = await createInvitationAccount(token)
      if (created.outcome === 'set_password' && typeof created.ticketUrl === 'string') {
        recordHandOver({ path, mode: 'setPassword' })
        leaveAppFor(created.ticketUrl)
      } else if (created.outcome === 'sign_in') {
        setView({ kind: 'choose', peek, notice: 'existing' })
      } else {
        setView({ kind: 'createError', peek })
      }
    } catch (error) {
      if (notUsable(error)) setView({ kind: 'invalid' })
      else if (status(error) === 429) setView({ kind: 'choose', peek, notice: 'busy' })
      else setView({ kind: 'createError', peek })
    }
  }

  const signOut = () => {
    recordHandOver({ path, mode: 'signOut' })
    endSession()
    void logout({ logoutParams: { returnTo: window.location.origin } })
  }

  const accept = async (details: InvitationDetails, refreshSession = false) => {
    setView({ kind: 'joining', details })
    try {
      const accessToken = refreshSession
        ? await getAccessTokenSilently({ cacheMode: 'off' })
        : await getAccessTokenSilently()
      const joined = await acceptInvitation(required(accessToken), token)
      const claims = await Promise.resolve(getIdTokenClaims()).catch(() => undefined)
      const sub = claims?.sub ?? user?.sub ?? ''
      const sid = typeof claims?.sid === 'string' ? claims.sid : ''
      openTenant({ sub, sid, tenantId: joined.tenantId })
      await mutate('bff/tenants', undefined, { revalidate: false })
      navigate('/', { replace: true, state: { joinedTenant: joined.tenantName } satisfies JoinedState })
    } catch (error) {
      const refusal = code(error)
      if (refusal === 'email_not_verified') setView({ kind: 'verify', details, still: refreshSession })
      else if (refusal === 'email_mismatch') setView({ kind: 'mismatch', details })
      else if (notUsable(error) || refusal === 'already_member' || refusal === 'landlord_email') setView({ kind: 'invalid' })
      else setView({ kind: 'error', retry: () => void accept(details, refreshSession) })
    }
  }

  if (isLoading || view.kind === 'loading') return <FullPageStatus text={t('people.invitation.loading')} />
  if (view.kind === 'settingUp') return <FullPageStatus text={t('people.invitation.settingUp')} />
  if (view.kind === 'joining') {
    return <FullPageStatus text={t('people.invitation.joining', { tenant: view.details.tenantName })} />
  }

  const notYou = (
    <Button variant="link" className="self-start px-0" onClick={signOut}>
      {t('people.invitation.notYou')}
    </Button>
  )

  let body: React.ReactNode
  switch (view.kind) {
    case 'choose': {
      const { peek } = view
      const returned =
        routeState?.invitationReturn === 'setPassword' ? RETURN_NOTICES[routeState.passwordResult ?? 'unknown'] : undefined
      const notice = view.notice ?? returned
      const shown = notice ? NOTICES[notice] : null
      const values = { masked: peek.maskedEmail, tenant: peek.tenantName }
      const signInButton = (
        <Button variant="primary" size="lg" className="w-full" onClick={() => signIn(notice === 'passwordSet')}>
          {t('people.invitation.signIn')}
        </Button>
      )
      body = (
        <>
          <div className="flex flex-col gap-2">
            <EntryTitle>{t('people.invitation.title', { tenant: peek.tenantName })}</EntryTitle>
            <p className="text-sm text-fg wrap-anywhere">{t('people.invitation.forEmail', { masked: peek.maskedEmail })}</p>
            <p className="text-sm text-fg-muted">{t('people.invitation.how', { tenant: peek.tenantName })}</p>
          </div>
          {notice && shown ? (
            <Notice tone={shown.tone} live title={t(`people.invitation.${notice}Title`)}>
              <span className="wrap-anywhere">{t(`people.invitation.${notice}Body`, values)}</span>
            </Notice>
          ) : null}
          {shown?.signInOnly ? (
            signInButton
          ) : (
            <div role="group" aria-label={t('people.invitation.choiceLabel')} className="flex flex-col gap-3">
              {signInButton}
              <Button variant="secondary" size="lg" className="w-full" onClick={() => void createAccount(peek)}>
                {t('people.invitation.createAccount')}
              </Button>
            </div>
          )}
          <p className="text-xs text-fg-muted">
            {t('people.invitation.until', { date: formatTenantDateTime(peek.validUntil, peek.timeZone) })}
          </p>
        </>
      )
      break
    }
    case 'confirm': {
      const { details } = view
      body = (
        <>
          {routeState?.accountReady ? (
            <Notice tone="info" live title={t('people.invitation.accountReadyTitle')}>
              {t('people.invitation.accountReadyBody', { tenant: details.tenantName })}
            </Notice>
          ) : null}
          <div className="flex flex-col gap-2">
            <EntryTitle>{t('people.invitation.title', { tenant: details.tenantName })}</EntryTitle>
            <p className="text-sm text-fg wrap-anywhere">
              {t('people.invitation.confirmBody', {
                inviter: details.inviterName,
                email: user?.email ?? details.maskedEmail,
                tenant: details.tenantName,
              })}
            </p>
            <div>
              <p className="text-xs font-medium text-fg-muted">
                {t('people.invitation.rolesLabel', { tenant: details.tenantName })}
              </p>
              <div className="mt-1">
                <RolePills roles={details.roles} />
              </div>
            </div>
          </div>
          <Button variant="primary" size="lg" className="w-full" onClick={() => void accept(details)}>
            {t('people.invitation.accept', { tenant: details.tenantName })}
          </Button>
          {notYou}
        </>
      )
      break
    }
    case 'verify': {
      const { details } = view
      body = (
        <>
          <div className="flex flex-col gap-2">
            <EntryTitle>{t('people.invitation.verifyTitle')}</EntryTitle>
            <p className="text-sm text-fg wrap-anywhere">
              {t('people.invitation.verifyBody', { email: user?.email ?? details.maskedEmail, tenant: details.tenantName })}
            </p>
          </div>
          {view.still ? <Notice tone="warning" live title={t('people.invitation.verifyStill')} /> : null}
          <Button variant="primary" size="lg" className="w-full" onClick={() => void accept(details, true)}>
            {t('people.invitation.verifyCheck')}
          </Button>
          {notYou}
        </>
      )
      break
    }
    case 'mismatch':
      body = (
        <>
          <div className="flex flex-col gap-2">
            <EntryTitle>{t('people.invitation.mismatchTitle')}</EntryTitle>
            <p className="text-sm text-fg wrap-anywhere">
              {t('people.invitation.mismatchBody', {
                signedIn: user?.email ?? user?.name ?? '',
                masked: view.details.maskedEmail,
              })}
            </p>
          </div>
          <Button variant="primary" size="lg" className="w-full" onClick={signOut}>
            {t('people.invitation.signOut')}
          </Button>
        </>
      )
      break
    case 'invalid':
      body = (
        <>
          <div className="flex flex-col gap-2">
            <EntryTitle>{t('people.invitation.invalidTitle')}</EntryTitle>
            <p className="text-sm text-fg">{t('people.invitation.invalidBody')}</p>
          </div>
          <Button variant="secondary" size="lg" className="w-full" onClick={() => navigate('/', { replace: true })}>
            {t('people.invitation.goToSignIn')}
          </Button>
        </>
      )
      break
    case 'createError': {
      const { peek } = view
      body = (
        <>
          <EntryTitle>{t('people.invitation.errorHeading')}</EntryTitle>
          <OperationFeedback
            state="error"
            title={t('people.invitation.createErrorTitle')}
            detail={t('people.invitation.createErrorBody')}
            onRetry={() => void createAccount(peek)}
          />
        </>
      )
      break
    }
    case 'error':
      body = (
        <>
          <EntryTitle>{t('people.invitation.errorHeading')}</EntryTitle>
          <OperationFeedback
            state="error"
            title={t('people.invitation.errorTitle')}
            detail={t('people.invitation.errorBody')}
            onRetry={view.retry}
          />
        </>
      )
      break
  }

  return (
    <EntryScreen>
      <div ref={rootRef} className="contents">
        <ProductMark />
        {body}
      </div>
    </EntryScreen>
  )
}

/*
 * Sits above AuthGate. When the app loads with an invitation hand-over
 * waiting (the return from Auth0), it goes back to the invitation page
 * instead, before AuthGate would send a person with no session to sign in.
 * Only the set-password page's `success` flag and a sign-in's `accountReady`
 * are carried back; the rest of the query is dropped. The checking screen
 * stays until that navigation unmounts this wrapper.
 */
export function HandOverReturn({ children }: { children: React.ReactNode }) {
  const t = useT()
  const navigate = useNavigate()
  const { search } = useLocation()
  const { isLoading } = useAuth0()
  const [waiting, setWaiting] = React.useState(() => hasHandOver())
  // Taken once, so a repeated effect run in development leaves the checking screen up.
  const started = React.useRef(false)

  React.useEffect(() => {
    if (!waiting || isLoading || started.current) return
    started.current = true
    const handOver = takeHandOver()
    if (!handOver) {
      setWaiting(false)
      return
    }
    const state: InvitationRouteState = { invitationReturn: handOver.mode }
    if (handOver.mode === 'setPassword') state.passwordResult = passwordResultOf(search)
    if (handOver.accountReady) state.accountReady = true
    navigate(handOver.path, { replace: true, state })
  }, [waiting, isLoading, navigate, search])

  if (waiting) return <FullPageStatus text={t('core.auth.checking')} />
  return <>{children}</>
}
